import { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router";

import { cellWidth } from "../../../core/methods/tableCellWidth";
import { all_routes } from "../../router/all_routes";
import Datatable from "../../../core/common/dataTable/index";
import TimelineModal from "../../../core/common/TimelineModal";

import {
  parseDate,
  parseNumber,
  safeLocaleCompare,
} from "../../../core/methods/sorterMethods";

import useApi from "../../../core/customhook/useApi";
import webservice_url, {
  apibaseurl,
} from "../../../core/webservice/Webservice_url";

import DynamicSingleSelect, {
  FilterTypes,
  FilterErrors,
  initialFilters,
} from "../reusable/DynamicSingleSelect";

import Loading from "../../../core/common/custom-loading/Loading";
import { formattedDate } from "../../../core/methods/formattedDate";
import { PaginationType } from "../../../types/common-type";
import useLocalStorage from "../../../core/customhook/hooks/useLocalStorage";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import Swal from "sweetalert2";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";
import { filterByColumnSearch } from "../utils/filterByColumnSearch";

export interface CaseTableRecord {
  key: string;
  sno: number;
  applicant_name: string;
  case_id: string;
  case_spoc: string;
  employee_id: string;
  case_received_date: string;
  sla_start_date: string;
  due_date: string;
  total_checks: number;
  work_in_progress: number;
  completed: number;
  stop: number;
  insuff_expire: string;
  actions: string;
}

export const actionOptions = [
  { value: "stop_case", label: "Stop Case" },
  { value: "stop_check", label: "Stop Check" },
  { value: "add_check", label: "Add Check" },
  { value: "generate_report", label: "Generate Report" },
];

type Props = {
  status_id: string;
};

const WorkInProgress: React.FC<Props> = ({ status_id }) => {
  const navigate = useNavigate();
  const { setLocalStorage, getLocalStorage } = useLocalStorage();
  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);
  const [filterErrors, setFilterErrors] = useState<FilterErrors>({});
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  const { callApi: searchCall, loading } = useApi<any>(
    webservice_url.fetch_live_case_data,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const DownloadAssestedReport = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
    responseType: "blob",
  });

  // const filteredData = (Array.isArray(allItemList) ? allItemList : []).filter(
  //   (item: any) => {
  //     return Object.entries(inventoryColumnSearch).every(([key, value]) => {
  //       if (!value) return true;

  //       return String(item[key] ?? "")
  //         .toLowerCase()
  //         .includes(value.toLowerCase());
  //     });
  //   },
  // );
  const filteredData = filterByColumnSearch(
    Array.isArray(allItemList) ? allItemList : [],
    inventoryColumnSearch,
    {
      assisted_checks: (r) =>
        `${r.verified_assisted_checks ?? 0} / ${r.assisted_checks ?? 0}`,
      non_assisted_checks: (r) =>
        `${r.verified_non_assisted_checks ?? 0} / ${r.non_assisted_checks ?? 0}`,
    },
  );

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;

    setShowTimeline(true);
    setTimelineLoading(true);

    try {
      const res = await getCaseLog.callApi({
        case_id: caseId,
      });

      setTimelineData(res?.data ?? []);
    } catch (error) {
      console.error("View Log Error:", error);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleDownloadReport = async (
    assignmentId: any,
    reportType: "assisted" | "non-assisted",
  ) => {
    const assignmentID = assignmentId?.assignment_id;

    if (!assignmentID) {
      console.error("Assignment ID not found");
      return;
    }

    try {
      const response: any = await DownloadAssestedReport.callApi(
        undefined,
        `${webservice_url.DownloadAssestedReport}/${assignmentID}/${reportType}-report/download-pdf`,
      );

      const blob = new Blob([response], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = `${reportType}_Report_${assignmentID}.pdf`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`${reportType} Report Download Error:`, error);
    }
  };

  const fetchData = async (
    filters: FilterTypes = filterData,
    page: number = pagination.current,
    pageSize: number = pagination.pageSize,
  ) => {
    try {
      const payload = {
        status: status_id,

        page: page,

        limit: pageSize,

        org_ids: Array.isArray(filters.organization)
          ? filters.organization
          : [],

        project_ids: Array.isArray(filters.project) ? filters.project : [],

        location_ids: Array.isArray(filters.location) ? filters.location : [],

        program_ids: Array.isArray(filters.program) ? filters.program : [],

        department_ids: Array.isArray(filters.department)
          ? filters.department
          : [],
      };

      const res = await searchCall(payload);

      if (res?.success) {
        setAllItemList(Array.isArray(res?.data?.rows) ? res.data.rows : []);

        setPagination((prev) => ({
          ...prev,

          total: res?.data?.pagination?.totalRecords ?? 0,

          current: page,

          pageSize: pageSize,
        }));
      } else {
        setAllItemList([]);

        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (error: any) {
      console.error("Live Case Fetch Error:", error?.response || error);

      setAllItemList([]);

      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    }
  };

  const handleTableChange = (pgConfig: any) => {
    const newPage = pgConfig.current;
    const newPageSize = pgConfig.pageSize;

    if (newPage !== pagination.current || newPageSize !== pagination.pageSize) {
      setPagination((prev) => ({
        ...prev,
        current: newPage,
        pageSize: newPageSize,
      }));
    }
  };
  const handleDownloadDrugReport = async (doc: any, record: any) => {
    const path = doc?.file_name;
    if (!path) return;

    const fileUrl = /^https?:\/\//i.test(path)
      ? path
      : `${apibaseurl}/uploads/${path}`;

    // extension original file se
    const ext = (doc.original_name || path).split(".").pop();

    // applicant_name + case_id, safe filename
    const baseName = [record?.applicant_name, record?.case_id]
      .filter(Boolean)
      .join("_")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^\w\-]/g, "");

    const fileName = baseName
      ? `${baseName}.${ext}`
      : doc.original_name || path;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Drug report download failed", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to download the drug report.",
      });
    }
  };

  useEffect(() => {
    const storedFilters = getLocalStorage("admin-live-case-filters");

    if (storedFilters) {
      const normalizedFilters: FilterTypes = {
        organization: Array.isArray(storedFilters?.organization)
          ? storedFilters.organization
          : storedFilters?.organization
            ? [storedFilters.organization]
            : [],

        project: Array.isArray(storedFilters?.project)
          ? storedFilters.project
          : storedFilters?.project
            ? [storedFilters.project]
            : [],

        location: Array.isArray(storedFilters?.location)
          ? storedFilters.location
          : storedFilters?.location
            ? [storedFilters.location]
            : [],

        program: Array.isArray(storedFilters?.program)
          ? storedFilters.program
          : storedFilters?.program
            ? [storedFilters.program]
            : [],

        department: Array.isArray(storedFilters?.department)
          ? storedFilters.department
          : storedFilters?.department
            ? [storedFilters.department]
            : [],
      };

      setFilterData(normalizedFilters);
    }
  }, []);

  useEffect(() => {
    fetchData(filterData, pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const handleFilterChange = (name: keyof FilterTypes, value: string[]) => {
    setFilterData((prev) => {
      const updated: FilterTypes = {
        ...prev,

        [name]: value,
      };

      /*
       * Organization changes
       * => clear all dependent fields
       */

      if (name === "organization") {
        updated.project = [];
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      /*
       * Account / Project changes
       * => clear Location, Program, Department
       */

      if (name === "project") {
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      /*
       * Location changes
       * => clear Program and Department
       */

      if (name === "location") {
        updated.program = [];
        updated.department = [];
      }

      /*
       * Program changes
       * => clear Department
       */

      if (name === "program") {
        updated.department = [];
      }

      return updated;
    });

    /*
     * Remove validation error as soon as
     * user selects something.
     */

    if (value.length > 0) {
      setFilterErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };
  const handleSearch = async () => {
    const errors: FilterErrors = {};

    /*
     * Organization is mandatory
     */

    if (!filterData.organization || filterData.organization.length === 0) {
      errors.organization = "Organization is required";
    }

    if (!filterData.project || filterData.project.length === 0) {
      errors.project = "Account is required";
    }

    setFilterErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLocalStorage("admin-live-case-filters", filterData);

    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    await fetchData(filterData, 1, pagination.pageSize);
  };

  const handleClearAll = async () => {
    setFilterData(initialFilters);

    setFilterErrors({});

    localStorage.removeItem("admin-live-case-filters");

    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));
      return;
    }

    await fetchData(initialFilters, 1, pagination.pageSize);
  };
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicant_name",
      onCell: () => cellWidth(200),
      render: (text: string, record: any) => (
        <div
          className="wip-link-cell"
          onClick={() => {
            navigate(all_routes.admin_caseDetails, {
              state: {
                case_id: record.case_id,
                assignment_id: record.assignment_id,
              },
            });
          }}
        >
          {text}
        </div>
      ),

      sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
    },
    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),

      dataIndex: "case_id",

      onCell: () => cellWidth(200),

      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: getSearchableHeader(
        "Candidate Id",
        "hrms_id",
        setInventoryColumnSearch,
      ),

      dataIndex: "hrms_id",

      onCell: () => cellWidth(200),

      sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
    },

    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc",
        setInventoryColumnSearch,
      ),

      dataIndex: "case_spoc",

      onCell: () => cellWidth(200),

      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },

    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "project_name",

      key: "project_name",

      onCell: () => cellWidth(200),

      width: "220px",

      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "program_name",

      key: "program_name",

      onCell: () => cellWidth(200),

      width: "220px",

      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "department_name",

      key: "department_name",

      onCell: () => cellWidth(200),

      width: "220px",

      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Case Received Date",
        "case_received_date",
        setInventoryColumnSearch,
      ),

      dataIndex: "case_received_date",

      key: "case_received_date",

      onCell: () => cellWidth(200),

      render: (text: string) => {
        const date = formattedDate(text) ?? "-";

        return <div>{date}</div>;
      },

      sorter: (a: any, b: any) =>
        parseDate(a.case_received_date).getTime() -
        parseDate(b.case_received_date).getTime(),
    },

    {
      title: getSearchableHeader(
        "SLA Start Date",
        "sla_start_date",
        setInventoryColumnSearch,
      ),

      dataIndex: "sla_start_date",

      key: "sla_start_date",

      onCell: () => cellWidth(200),

      render: (text: string) => {
        const date = formattedDate(text) ?? "-";

        return <div>{date}</div>;
      },

      sorter: (a: any, b: any) =>
        parseDate(a.sla_start_date).getTime() -
        parseDate(b.sla_start_date).getTime(),
    },

    {
      title: getSearchableHeader(
        "Due Date",
        "due_date",
        setInventoryColumnSearch,
      ),

      dataIndex: "due_date",

      key: "due_date",

      onCell: () => cellWidth(200),

      render: (text: string) => {
        const date = formattedDate(text) ?? "-";

        return <div>{date}</div>;
      },

      sorter: (a: any, b: any) =>
        parseDate(a.due_date).getTime() - parseDate(b.due_date).getTime(),
    },

    {
      title: getSearchableHeader(
        "Total Checks",
        "total_checks",
        setInventoryColumnSearch,
      ),

      dataIndex: "total_checks",

      key: "total_checks",

      align: "center",

      onCell: () => cellWidth(200),

      sorter: (a: any, b: any) =>
        parseNumber(a.total_checks) - parseNumber(b.total_checks),
    },

    {
      title: getSearchableHeader(
        "Assisted",
        "assisted_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "assisted_checks",

      key: "assisted_checks",

      onCell: () => cellWidth(200),

      align: "center",

      sorter: (a, b) =>
        Number(a.assisted_checks ?? 0) - Number(b.assisted_checks ?? 0),

      render: (_value: number, record: any) => {
        const verified = Number(record.verified_assisted_checks ?? 0);

        const total = Number(record.assisted_checks ?? 0);

        /*
         * 0 / 0 should NOT be Complete.
         */

        const isComplete = total > 0 && verified === total;

        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },

    {
      title: getSearchableHeader(
        "Non-Assisted",
        "non_assisted_checks",
        setInventoryColumnSearch,
      ),

      dataIndex: "non_assisted_checks",
      key: "non_assisted_checks",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) =>
        Number(a.non_assisted_checks ?? 0) - Number(b.non_assisted_checks ?? 0),
      render: (_value: number, record: any) => {
        const verified = Number(record.verified_non_assisted_checks ?? 0);
        const total = Number(record.non_assisted_checks ?? 0);
        const isComplete = total > 0 && verified === total;
        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },

    {
      title: getSearchableHeader(
        "Candidate Submited",
        "candidate_submit",
        setInventoryColumnSearch,
      ),

      dataIndex: "candidate_submit",

      key: "candidate_submit",

      onCell: () => cellWidth(200),

      align: "center",

      sorter: (a, b) =>
        Number(a.candidate_submit ?? 0) - Number(b.candidate_submit ?? 0),

      render: (value: number) => (
        <span className="wip-chip wip-chip-success">{value ?? 0}</span>
      ),
    },

    {
      title: getSearchableHeader(
        "Work In Progress",
        "work_in_progress",
        setInventoryColumnSearch,
      ),

      dataIndex: "work_in_progress",
      onCell: () => cellWidth(200),
      key: "work_in_progress",

      align: "center",

      sorter: (a: any, b: any) =>
        parseNumber(a.work_in_progress) - parseNumber(b.work_in_progress),
    },

    {
      title: getSearchableHeader(
        "Completed",
        "completed",
        setInventoryColumnSearch,
      ),
      onCell: () => cellWidth(180),

      dataIndex: "completed",

      key: "completed",

      align: "center",

      sorter: (a: any, b: any) =>
        parseNumber(a.completed) - parseNumber(b.completed),
    },

    {
      title: getSearchableHeader("Stop", "stopped", setInventoryColumnSearch),

      dataIndex: "stopped",
      onCell: () => cellWidth(180),

      key: "stopped",

      align: "center",

      sorter: (a: any, b: any) =>
        parseNumber(a.stopped) - parseNumber(b.stopped),
    },

    {
      title: getSearchableHeader(
        "Drug Test",
        "drug_test_status",
        setInventoryColumnSearch,
      ),
      onCell: () => cellWidth(200),
      dataIndex: "drug_test_status",
      key: "drug_test_status",
      align: "center",
      sorter: (a, b) => a.drug_test_status - b.drug_test_status,
    },

    {
      title: "Drug Report",
      dataIndex: "drug_report",
      key: "drug_report",
      align: "center",
      onCell: () => cellWidth(130),
      sorter: (a, b) =>
        (a.drug_report?.length ?? 0) - (b.drug_report?.length ?? 0),
      render: (docs: any[] | null, record: any) =>
        Array.isArray(docs) && docs.length > 0 ? (
          <div className="d-flex flex-column align-items-center gap-1">
            {docs.map((doc) => (
              <div
                key={doc.document_id}
                className="d-flex align-items-center gap-2"
              >
                <span
                  onClick={() => handleDownloadDrugReport(doc, record)}
                  className="wip-chip wip-chip-success"
                  style={{ cursor: "pointer" }}
                >
                  {doc.original_name ?? doc.file_name}{" "}
                  <i className="ti ti-download" />
                </span>
              </div>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Assisted Report",

      dataIndex: "",

      onCell: () => cellWidth(130),

      render: (_: any, record: any) => (
        <button
          type="button"
          className="btn btn-sm btn-primary d-flex"
          onClick={() => handleDownloadReport(record, "assisted")}
          disabled={loading || !record.show_assisted_report}
        >
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },

    {
      title: "Non-assisted Report",
      dataIndex: "",
      onCell: () => cellWidth(140),
      align:"center",
      render: (_: any, record: any) => (
        <button
          type="button"
          className="btn btn-sm btn-primary d-flex"
          onClick={() => handleDownloadReport(record, "non-assisted")}
          disabled={loading || !record.show_nonassisted_report}
        >
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },
    {
      title: "View Log",

      key: "viewLog",

      onCell: () => cellWidth(110),

      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Work_In_Progress_Report",
      sheetName: "Work In Progress",
    });
  };

  return (
    <div className="px-2">
      <DynamicSingleSelect
        fields={[
          "organization",
          "project",
          "location",
          "program",
          "department",
        ]}
        filterData={filterData}
        onFilterDataChange={(name, value, _labels) => {
          handleFilterChange(name, value);
        }}
        parentError={filterErrors}
        showButton={true}
        buttonText="Search"
        onButtonClick={handleSearch}
        disableButton={loading}
        onClearAll={handleClearAll}
      />

      {/* Table */}
      <div className="card mt-3">
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              dataSource={filteredData}
              columns={columns}
              fixedHeader
              tableHeight={650}
              Selection={false}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* Timeline Modal */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
    </div>
  );
};

export default WorkInProgress;
